import { Link } from 'react-router-dom';

import { ChevronRightIcon, CloseIcon, InfoIcon } from '@/components/icons/registry';
import { calloutVariants } from '@/components/ui/callout';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

/** Onde o cadastro da conta anônima continua (`modules/auth`, DV-24). */
const FINISH_SIGN_UP_ROUTE = '/register/finish';

interface SignUpReminderProps {
  onDismiss: () => void;
  className?: string;
}

/**
 * Convite para terminar o cadastro, para quem pediu socorro sem conta.
 *
 * Só apresentação: quando aparece e quanto tempo a dispensa vale é do
 * `useSignUpReminder`, e onde ele flutua é do `AppLayout`.
 *
 * ## Por que não é o `Callout` em si
 *
 * A cor e a borda **são** as do `Callout` `info` (`calloutVariants`), pares já
 * medidos em AA. O que muda é a forma: o `Callout` é bloco de leitura dentro do
 * conteúdo, com o texto parado e um link opcional embaixo. Aqui o aviso flutua
 * sobre o mapa e a barra, e a altura é o recurso escasso — ele inteiro precisa
 * ser o alvo do toque, numa faixa só. Um `Callout` com um botão dentro teria o
 * dobro da altura para dizer a mesma coisa.
 *
 * ## Desenho
 *
 * - O corpo é um **link** para `/register/finish`, com altura ≥ 44px, e o
 *   chevron diz que ele leva a algum lugar. O título carrega a ação; a linha de
 *   apoio diz o que se perde sem ela, que é o que move alguém a tocar.
 * - O "X" é um alvo separado de 44px, ao lado e não dentro do link — um toque
 *   para dispensar nunca pode abrir o cadastro, nem o contrário.
 * - A altura é limitada (título em uma linha, apoio em até duas) porque a
 *   reserva que as telas fazem para ele é um número fixo
 *   (`--signup-reminder-inset`, `src/index.css`).
 * - `shadow-lg` porque ele flutua sobre tiles: sem sombra, o azul claro encosta
 *   num quarteirão claro do mapa e a borda some — o mesmo motivo da sombra nos
 *   controles do mapa.
 */
export function SignUpReminder({ onDismiss, className }: SignUpReminderProps) {
  return (
    <aside
      aria-label="Cadastro incompleto"
      className={cn(
        calloutVariants({ variant: 'info' }),
        'items-center gap-1 py-1 pr-1 pl-3 shadow-lg',
        className,
      )}
    >
      <Link
        to={FINISH_SIGN_UP_ROUTE}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-lg py-1.5"
      >
        <Icon as={InfoIcon} size="md" className="shrink-0" />

        <span className="flex min-w-0 flex-1 flex-col">
          <Text variant="subtitle" as="span" truncate className="block">
            Termine seu cadastro
          </Text>
          <Text variant="caption" as="span" className="line-clamp-2">
            Para não perder o acompanhamento do pedido.
          </Text>
        </span>

        <Icon as={ChevronRightIcon} size="md" className="shrink-0" />
      </Link>

      <button
        type="button"
        onClick={onDismiss}
        // O rótulo conta o que o toque faz de verdade: o aviso volta amanhã.
        aria-label="Lembrar depois"
        className="active:bg-info/10 grid size-11 shrink-0 place-items-center rounded-lg"
      >
        <Icon as={CloseIcon} size="md" />
      </button>
    </aside>
  );
}
